/**
 * Patient Appointments — list and filter appointments.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { appointmentAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const statusColors = {
  scheduled: 'badge-primary',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const PatientAppointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await appointmentAPI.list();
        setAppointments(Array.isArray(res.data) ? res.data : (res.data?.appointments || []));
      } catch {
        toast.error('Failed to load appointments.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Live updates: refresh whenever appointment status changes server-side
  const { on } = useNotifications();
  useEffect(() => {
    return on('appointment_update', () => {
      appointmentAPI.list()
        .then(res => setAppointments(Array.isArray(res.data) ? res.data : (res.data?.appointments || [])))
        .catch(() => {});
    });
  }, [on]);

  useEffect(() => {
    return on('consultation_ready', () => {
      appointmentAPI.list()
        .then(res => setAppointments(Array.isArray(res.data) ? res.data : (res.data?.appointments || [])))
        .catch(() => {});
    });
  }, [on]);

  const filtered = filter === 'all' ? appointments : appointments.filter(a => a.status === filter);

  return (
    <Layout title="Appointments" role="patient" showBack>
      <div className="p-4 space-y-4 pb-24">
        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {['all', 'scheduled', 'in_progress', 'completed', 'cancelled'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === f ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </button>
          ))}
        </div>

        {/* Quick book */}
        <Link to="/patient/appointments/book" className="card flex items-center gap-3 bg-primary-50 border-primary-200 hover:shadow-md transition-shadow">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <span className="text-sm font-medium text-primary-700">Book New Appointment</span>
        </Link>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card text-center py-8 text-gray-400">
            <p className="text-sm">No {filter !== 'all' ? filter.replace('_', ' ') : ''} appointments found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(apt => (
              <div key={apt.id} className="card hover:shadow-md transition-shadow">
                <Link to={`/patient/appointments/${apt.id}`} className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">Dr. {apt.doctor_name || 'TBD'}</p>
                    <p className="text-xs text-gray-500">{apt.appointment_type || apt.type || 'Consultation'} • {apt.appointment_date ? new Date(apt.appointment_date).toLocaleDateString() : '—'}</p>
                    {apt.queue_position && (
                      <p className="text-xs text-gray-400">Queue position: #{apt.queue_position}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[apt.status] || 'bg-gray-100 text-gray-800'}`}>
                    {apt.status}
                  </span>
                </Link>
                {apt.doctor_uid && (
                  <div className="mt-2 pt-2 border-t border-gray-100 flex justify-end gap-2">
                    {apt.room_id && apt.status === 'in_progress' && (
                      <button
                        onClick={() => navigate(`/consultation?room=${apt.room_id}&appointment=${apt.id}`)}
                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Join Video Call
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/patient/doctors/${apt.doctor_uid}`)}
                      className="px-3 py-1.5 bg-primary-50 text-primary-700 text-xs font-medium rounded-lg hover:bg-primary-100 transition-colors"
                    >
                      View Doctor Profile
                    </button>
                  </div>
                )}
                {/* Pay Now banner for unpaid scheduled appointments */}
                {apt.payment_status !== 'paid' && ['scheduled', 'confirmed'].includes(apt.status) && (
                  <div className="mt-2 pt-2 border-t border-orange-100 flex items-center justify-between">
                    <p className="text-xs text-orange-700 font-medium">⚠ Payment pending — doctor cannot see this booking until paid</p>
                    <Link
                      to={`/patient/payment?appointment=${apt.id}&amount=${apt.consultation_fee || 500}`}
                      className="ml-2 flex-shrink-0 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Pay Now
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PatientAppointments;
